import { sizeGrids } from './size-grid.js';
import type { GridBlock, GridVariant } from './size-grid.js';

/**
 * What a receiving line will accept (SCOPE §6.1).
 *
 * Pure, because these are the decisions that were getting made inline in JSX
 * and getting made wrong: a line that was fully received still offered a count
 * box, and a partly-received line nearly lost one. Both are quantity errors
 * that end up in inventory, so they are worth asserting rather than eyeballing.
 */

export interface ReceivingLineCounts {
  /** Units on the purchase order. */
  readonly ordered: number;
  /** Units received on earlier, already-committed receipts. */
  readonly previouslyReceived: number;
  /** Units the buyer gave up on — a cancelled backorder. */
  readonly cancelled: number;
  /** Units counted on the receipt being worked on now. */
  readonly receivingNow: number;
}

/**
 * How many units this line can still take, ignoring the current count.
 *
 * Deliberately excludes `receivingNow`: it is the ceiling for this receipt, and
 * it must not move as the operator types into it. 3 ordered with 2 already in
 * leaves 1 receivable whether or not that 1 has been counted yet.
 */
export function receivableUnits(line: ReceivingLineCounts): number {
  return Math.max(line.ordered - line.previouslyReceived - line.cancelled, 0);
}

/**
 * Whether this line is finished and should stop offering a count box.
 *
 * A box on a settled line invites a count that cannot be applied — the operator
 * types 3, nothing changes, and they cannot tell a full line from a broken app.
 */
export function isLineSettled(line: ReceivingLineCounts): boolean {
  return receivableUnits(line) === 0;
}

/** Still owed after this receipt commits — what stays on backorder. */
export function outstandingAfterCommit(line: ReceivingLineCounts): number {
  return Math.max(receivableUnits(line) - line.receivingNow, 0);
}

/** The line is fully counted in, so the "all arrived" tick reads as on. */
export function isFullyCounted(line: ReceivingLineCounts): boolean {
  return line.receivingNow === receivableUnits(line);
}

/**
 * What the "all arrived" tick sets the count to.
 *
 * A toggle, not a one-way door (§2.2-11): ticking by mistake is undone by
 * ticking again, without having to remember what the number was.
 */
export function toggleAllArrived(line: ReceivingLineCounts): number {
  return isFullyCounted(line) ? 0 : receivableUnits(line);
}

/**
 * A typed count, read as a number.
 *
 * An empty box means zero rather than "leave it alone", so clearing a field is
 * a way to undo a miscount. Anything unparseable holds the current value —
 * silently sending NaN to the server would be a lot worse.
 */
export function parseCount(draft: string, current: number): number {
  if (draft.trim() === '') return 0;
  const parsed = Number.parseInt(draft, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : current;
}

/**
 * The bench's lines as one grid per style (2026-10-01).
 *
 * The client: "when receiving the items should be in grid when we are marking
 * the count in … find the item, then it's got all colors and sizes in grid" —
 * and a style that comes in lengths, Sugarbush, has to be counted length by
 * length. The style page and the order grid already lay a style out this way
 * (`sizeGrids`), so receiving uses the same helper: colors down, sizes across,
 * one grid per length. Styles keep the order the lines arrive in, which is
 * the order's own.
 */
export interface StyleKey {
  readonly productId: string;
  readonly productName: string;
  readonly styleNumber: string;
}

export interface ReceivingStyle<T> extends StyleKey {
  readonly blocks: GridBlock<T>[];
}

export function receivingStyles<T extends StyleKey & GridVariant>(
  lines: readonly T[],
): ReceivingStyle<T>[] {
  const byStyle = new Map<string, T[]>();
  for (const line of lines) {
    const group = byStyle.get(line.productId);
    if (group === undefined) byStyle.set(line.productId, [line]);
    else group.push(line);
  }
  return [...byStyle.values()].map((group) => ({
    productId: group[0]!.productId,
    productName: group[0]!.productName,
    styleNumber: group[0]!.styleNumber,
    blocks: sizeGrids(group),
  }));
}
